'use client'

import React, { useEffect, useRef } from 'react'
import { trackCtaImpression, trackGooglePlayClick, type EventParams } from '@/utils/analytics'
import { googlePlayHref } from '@/utils/googlePlay'

/**
 * Centralized Google Play link, the Android twin of AppStoreLink. It sits
 * beside every App Store CTA with the same `source` label, so impressions and
 * clicks for both stores break down by the same surface.
 */
export interface GooglePlayLinkProps {
  source: string
  ctaPosition?: string
  trackingParams?: EventParams
  className?: string
  children: React.ReactNode
  'aria-label'?: string
}

export default function GooglePlayLink({
  source,
  ctaPosition,
  trackingParams,
  className,
  children,
  'aria-label': ariaLabel,
}: GooglePlayLinkProps) {
  const linkRef = useRef<HTMLAnchorElement>(null)
  const impressionTrackedRef = useRef(false)

  useEffect(() => {
    const element = linkRef.current
    if (!element || impressionTrackedRef.current) return

    const fire = () => {
      if (impressionTrackedRef.current) return
      impressionTrackedRef.current = true
      trackCtaImpression(source, {
        ...(ctaPosition ? { cta_position: ctaPosition } : {}),
        cta_component: 'google_play_link',
        destination: 'google_play',
        destination_type: 'google_play',
        ...(trackingParams ?? {}),
      })
    }

    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: [0.5] },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [source, ctaPosition, trackingParams])

  return (
    <a
      ref={linkRef}
      href={googlePlayHref(source)}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={ariaLabel}
      onClick={() =>
        trackGooglePlayClick(source, {
          ...(ctaPosition ? { cta_position: ctaPosition } : {}),
          ...(trackingParams ?? {}),
        })
      }
    >
      {children}
    </a>
  )
}
